import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, CheckCircle2, Hash, Search, ScanLine } from "lucide-react";
import KioskLayout from "@/kiosk/KioskLayout";
import kioskApi, { getKioskToken } from "@/lib/kioskApi";
import axios from "axios";
import { API } from "@/lib/api";

const TABS = [
  { key: "name", label: "By name", icon: Search, testid: "tab-name" },
  { key: "qr", label: "By QR", icon: ScanLine, testid: "tab-qr" },
  { key: "pin", label: "By PIN", icon: Hash, testid: "tab-pin" },
];

function readKioskToken() {
  const tok = getKioskToken();
  if (!tok) return null;
  try { return JSON.parse(atob(tok.split(".")[1])); } catch { return null; }
}

export default function KioskSignOut() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const tokenInfo = readKioskToken();
  const [branding, setBranding] = useState(null);
  const [tab, setTab] = useState("name");
  const [signedIn, setSignedIn] = useState([]);
  const [nameQ, setNameQ] = useState("");
  const [pin, setPin] = useState("");
  const [qrToken, setQrToken] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!tokenInfo) {
      nav("/kiosk/pair", { replace: true });
      return;
    }
    (async () => {
      try {
        const b = await axios.get(`${API}/sites/${tokenInfo.site_id}/public-branding`);
        setBranding(b.data);
        const r = await kioskApi.get("/visits/lookup", { params: { signed_in_only: true } });
        setSignedIn(r.data);
      } catch (e) {
        /* ignore */
      }
    })();
  }, [nav, tokenInfo?.site_id]);

  const filtered = signedIn.filter((v) => v.visitor_name?.toLowerCase().includes(nameQ.toLowerCase()));

  const finishWith = (msg = "") => {
    setDone(true);
    setTimeout(() => nav("/kiosk", { replace: true }), 4000);
  };

  const signOutId = async (id) => {
    setErr("");
    try {
      await kioskApi.post("/visits/sign-out", { visit_id: id });
      finishWith();
    } catch (e) {
      setErr(e.response?.data?.detail || "Sign-out failed");
    }
  };
  const signOutPin = async () => {
    setErr("");
    try {
      await kioskApi.post("/visits/sign-out/pin", { pin });
      finishWith();
    } catch (e) {
      setErr(e.response?.data?.detail || "Invalid PIN");
    }
  };
  const signOutQr = async () => {
    setErr("");
    try {
      await kioskApi.post("/visits/sign-out/qr", { qr_token: qrToken });
      finishWith();
    } catch (e) {
      setErr(e.response?.data?.detail || "Invalid QR");
    }
  };

  if (done) {
    return (
      <KioskLayout branding={branding}>
        <div className="flex-1 flex flex-col items-center justify-center text-center" data-testid="kiosk-signout-done">
          <CheckCircle2 className="w-16 h-16 text-green-300 mb-4" />
          <h2 className="text-4xl font-bold">{t("sign_out_success")}</h2>
        </div>
      </KioskLayout>
    );
  }

  return (
    <KioskLayout branding={branding}>
      <div className="flex items-center justify-between mb-6 text-white/70 text-sm">
        <button onClick={() => nav("/kiosk", { replace: true })} className="inline-flex items-center gap-2 hover:text-white" data-testid="signout-back">
          <ArrowLeft className="w-4 h-4" /> {t("back")}
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center max-w-2xl w-full mx-auto" data-testid="kiosk-sign-out-page">
        <h2 className="text-3xl md:text-4xl font-semibold mb-6 text-center">{t("sign_out")}</h2>

        <div className="bg-white/5 border border-white/15 rounded-full p-1 inline-flex gap-1 mb-8">
          {TABS.map((tt) => {
            const Icon = tt.icon;
            return (
              <button
                key={tt.key}
                onClick={() => setTab(tt.key)}
                data-testid={tt.testid}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                  tab === tt.key ? "bg-orange-500 text-white" : "text-white/70 hover:text-white"
                }`}
              >
                <Icon className="w-4 h-4" /> {tt.label}
              </button>
            );
          })}
        </div>

        {err && <div className="text-red-300 mb-4 text-sm" data-testid="signout-error">{err}</div>}

        {tab === "name" && (
          <div className="w-full" data-testid="signout-name-pane">
            <input
              value={nameQ}
              onChange={(e) => setNameQ(e.target.value)}
              placeholder={t("sign_out_search_placeholder")}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-4 text-lg text-white placeholder-white/40 focus:outline-none focus:border-orange-400"
              data-testid="signout-name-input"
            />
            <div className="mt-4 space-y-2 max-h-[50vh] overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="text-white/50 text-sm text-center py-8">{t("no_results")}</p>
              ) : filtered.map((v) => (
                <button
                  key={v.id}
                  onClick={() => signOutId(v.id)}
                  className="w-full text-left rounded-lg bg-white/5 hover:bg-white/10 border border-white/15 p-4"
                  data-testid={`signout-row-${v.id}`}
                >
                  <div className="font-semibold">{v.visitor_name}</div>
                  <div className="text-xs text-white/50">{v.visitor_company || "—"} · in since {v.signed_in_at && new Date(v.signed_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {tab === "pin" && (
          <div className="w-full max-w-sm" data-testid="signout-pin-pane">
            <input
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              inputMode="numeric"
              maxLength={4}
              placeholder={t("pin_placeholder")}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-5 text-center text-4xl tracking-[0.5em] font-bold text-white placeholder-white/40 focus:outline-none focus:border-orange-400"
              data-testid="signout-pin-input"
            />
            <button
              onClick={signOutPin}
              disabled={pin.length !== 4}
              className="w-full mt-4 inline-flex items-center justify-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 font-semibold disabled:opacity-50"
              data-testid="signout-pin-submit"
            >Sign me out</button>
          </div>
        )}

        {tab === "qr" && (
          <div className="w-full max-w-md text-center" data-testid="signout-qr-pane">
            <p className="text-white/70 mb-4">Scan your badge QR with the camera, or paste the QR token below.</p>
            <textarea
              value={qrToken}
              onChange={(e) => setQrToken(e.target.value)}
              placeholder="Paste QR token here…"
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 font-mono text-xs text-white placeholder-white/40 focus:outline-none focus:border-orange-400"
              rows={4}
              data-testid="signout-qr-input"
            />
            <button
              onClick={signOutQr}
              disabled={!qrToken.trim()}
              className="w-full mt-4 inline-flex items-center justify-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 font-semibold disabled:opacity-50"
              data-testid="signout-qr-submit"
            >Sign me out</button>
          </div>
        )}
      </div>
    </KioskLayout>
  );
}
